import axios from "axios";
import myConfig from "../../configs/config";

function authHeaders(extra = {}) {
  return {
    Authorization: `Bearer ${localStorage.getItem("client-token")}`,
    ...extra,
  };
}

function normalizeOperator(raw) {
  if (!raw || typeof raw !== "object") return null;
  const id = raw.id != null ? raw.id : raw.pk;
  if (id == null) return null;
  const name =
    (raw.name || raw.nome || raw.ident || `Operador ${id}`).toString().trim() ||
    `Operador ${id}`;
  const ident = (raw.ident || raw.identification || "").toString();
  return { id: Number(id), name, ident };
}

export async function fetchRadioGroups(farmId) {
  const response = await axios.get(
    `${myConfig.BE_URL}/api/v1/mobile/radio/groups/`,
    {
      params: { farm_id: farmId },
      headers: authHeaders(),
    }
  );
  return response.data;
}

export async function createRadioGroup(farmId, payload) {
  const response = await axios.post(
    `${myConfig.BE_URL}/api/v1/mobile/radio/groups/?farm_id=${farmId}`,
    { ...payload, farm_id: farmId },
    { headers: authHeaders({ "Content-Type": "application/json" }) }
  );
  return response.data;
}

export async function updateRadioGroup(farmId, groupId, payload) {
  const response = await axios.patch(
    `${myConfig.BE_URL}/api/v1/mobile/radio/groups/${groupId}/?farm_id=${farmId}`,
    payload,
    { headers: authHeaders({ "Content-Type": "application/json" }) }
  );
  return response.data;
}

export async function deleteRadioGroup(farmId, groupId) {
  await axios.delete(
    `${myConfig.BE_URL}/api/v1/mobile/radio/groups/${groupId}/?farm_id=${farmId}`,
    { headers: authHeaders() }
  );
}

export async function fetchRadioMessages(farmId, params = {}) {
  const response = await axios.get(
    `${myConfig.BE_URL}/api/v1/mobile/radio/messages/`,
    {
      params: { farm_id: farmId, limit: 50, ...params },
      headers: authHeaders(),
    }
  );
  return response.data;
}

/** Garante URL absoluta do áudio usando BE_URL (app grava .m4a, web grava .webm). */
export function normalizeRadioAudioUrl(url) {
  if (!url) return null;
  try {
    const base = (myConfig.BE_URL || "").replace(/\/$/, "");
    if (url.startsWith("http://") || url.startsWith("https://")) {
      const parsed = new URL(url);
      if (parsed.pathname.startsWith("/media/")) {
        if (
          typeof window !== "undefined" &&
          (window.location.hostname === "localhost" ||
            window.location.hostname === "127.0.0.1")
        ) {
          return parsed.pathname;
        }
        if (base) {
          const baseUrl = new URL(base);
          const loopback =
            parsed.hostname === "127.0.0.1" || parsed.hostname === "localhost";
          const baseLoopback =
            baseUrl.hostname === "127.0.0.1" ||
            baseUrl.hostname === "localhost";
          if (
            parsed.origin !== baseUrl.origin ||
            (loopback && baseLoopback && parsed.hostname !== baseUrl.hostname)
          ) {
            return baseUrl.origin + parsed.pathname;
          }
        }
      }
      return url;
    }
    if (url.startsWith("/media/")) return url;
    if (!base) return url;
    return base + (url.startsWith("/") ? url : "/" + url);
  } catch (e) {
    return url;
  }
}

export function radioAudioMimeType(url) {
  if (!url) return undefined;
  const lower = url.toLowerCase();
  if (lower.endsWith(".m4a") || lower.endsWith(".mp4")) {
    return "audio/mp4; codecs=mp4a.40.2";
  }
  if (lower.endsWith(".webm")) return "audio/webm; codecs=opus";
  if (lower.endsWith(".ogg")) return "audio/ogg; codecs=opus";
  if (lower.endsWith(".wav")) return "audio/wav";
  return undefined;
}

export function isTabletM4aUrl(url) {
  const normalized = normalizeRadioAudioUrl(url);
  if (!normalized) return false;
  const lower = normalized.toLowerCase();
  return lower.includes(".m4a") || lower.includes(".mp4");
}

export function isWebmRadioUrl(url) {
  const normalized = normalizeRadioAudioUrl(url);
  if (!normalized) return false;
  return normalized.toLowerCase().includes(".webm");
}

function writeWavString(view, offset, string) {
  for (let i = 0; i < string.length; i++) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
}

export function encodeWavFromAudioBuffer(audioBuffer) {
  const numChannels = audioBuffer.numberOfChannels;
  const sampleRate = audioBuffer.sampleRate;
  const bitsPerSample = 16;
  const bytesPerSample = bitsPerSample / 8;
  const blockAlign = numChannels * bytesPerSample;
  const length = audioBuffer.length;
  const dataSize = length * blockAlign;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  writeWavString(view, 0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeWavString(view, 8, "WAVE");
  writeWavString(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitsPerSample, true);
  writeWavString(view, 36, "data");
  view.setUint32(40, dataSize, true);

  let offset = 44;
  for (let i = 0; i < length; i++) {
    for (let ch = 0; ch < numChannels; ch++) {
      const sample = Math.max(-1, Math.min(1, audioBuffer.getChannelData(ch)[i]));
      view.setInt16(
        offset,
        sample < 0 ? sample * 0x8000 : sample * 0x7fff,
        true
      );
      offset += 2;
    }
  }
  return new Blob([buffer], { type: "audio/wav" });
}

export async function convertBlobToWav(blob) {
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) {
    throw new Error("AudioContext indisponível");
  }
  const ctx = new AudioCtx();
  const arrayBuffer = await blob.arrayBuffer();
  const audioBuffer = await ctx.decodeAudioData(arrayBuffer.slice(0));
  try {
    ctx.close();
  } catch (e) {}
  return encodeWavFromAudioBuffer(audioBuffer);
}

/** Baixa áudio como Blob (fallback se a URL direta falhar). */
export async function fetchRadioAudioBlob(url) {
  const src = normalizeRadioAudioUrl(url);
  if (!src) throw new Error("URL de áudio inválida");

  const response = await fetch(src, { credentials: "same-origin" });
  if (!response.ok) {
    throw new Error("Falha ao baixar áudio (" + response.status + ")");
  }

  let blob = await response.blob();
  const lower = (url || src).toLowerCase();
  if (lower.includes(".m4a") || lower.includes(".mp4")) {
    if (!blob.type || blob.type === "application/octet-stream") {
      blob = new Blob([blob], { type: "audio/mp4" });
    }
  } else if (lower.includes(".webm")) {
    if (
      !blob.type ||
      blob.type === "video/webm" ||
      blob.type === "application/octet-stream"
    ) {
      blob = new Blob([blob], { type: "audio/webm" });
    }
  } else if (lower.includes(".wav")) {
    if (!blob.type || blob.type === "application/octet-stream") {
      blob = new Blob([blob], { type: "audio/wav" });
    }
  }
  return blob;
}

export async function uploadRadioMessage(farmId, { file, metadata }) {
  const form = new FormData();
  form.append("metadata", JSON.stringify({ ...metadata, farm_id: farmId }));
  form.append("file", file);
  const response = await axios.post(
    `${myConfig.BE_URL}/api/v1/mobile/radio/messages/?farm_id=${farmId}`,
    form,
    {
      headers: authHeaders({ "Content-Type": "multipart/form-data" }),
    }
  );
  return response.data;
}

/**
 * Lista operadores da fazenda (mesmo endpoint do Cadastros → Operadores),
 * com fallback para field-data/filters.
 */
export async function fetchFarmOperators(farmId) {
  const byId = new Map();

  try {
    const response = await axios.get(`${myConfig.API_URL}/operator/`, {
      params: { farm_id: farmId },
      headers: authHeaders(),
    });
    const data = response.data;
    const list = Array.isArray(data)
      ? data
      : data.results || data.operators || [];
    list.forEach((raw) => {
      const op = normalizeOperator(raw);
      if (op) byId.set(op.id, op);
    });
  } catch (error) {
    console.warn("operator/ falhou, tentando fallback", error);
  }

  if (byId.size === 0) {
    try {
      const response = await axios.get(
        `${myConfig.BE_URL}/api/v1/mobile/field-data/filters/`,
        {
          params: { farm_id: farmId },
          headers: authHeaders(),
        }
      );
      (response.data.operators || []).forEach((raw) => {
        const op = normalizeOperator(raw);
        if (op) byId.set(op.id, op);
      });
    } catch (error) {
      console.warn("field-data/filters operadores falhou", error);
    }
  }

  return Array.from(byId.values()).sort((a, b) =>
    a.name.localeCompare(b.name, "pt-BR")
  );
}
